import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, Send, RotateCcw, ExternalLink, Copy, Bot, User, Sparkles,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AgentDemo() {
  const { id } = useParams();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [convId, setConvId] = useState(null);
  const scrollerRef = useRef(null);

  const shareUrl = `${BACKEND}/demo/agent/${id}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/agents/${id}`);
        setAgent(data);
        if (data.greeting) {
          setMessages([{ role: "assistant", content: data.greeting }]);
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post(`/public/agents/${id}/chat`, {
        message: text, conversation_id: convId,
      });
      if (data.conversation_id) setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: `[Error] ${formatApiError(err)}` }]);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setMessages(agent?.greeting ? [{ role: "assistant", content: agent.greeting }] : []);
    setConvId(null);
    toast.success("Conversation reset.");
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Demo link copied.");
    } catch { toast.error("Copy failed."); }
  };

  const quickPrompts = [
    "What do you do?",
    "How much does it cost?",
    "Can I book a demo?",
    "Do you work with plumbers?",
  ];

  if (loading) return <div className="p-10 text-neutral-500">Loading agent…</div>;
  if (!agent) return <div className="p-10 text-neutral-500">Agent not found.</div>;

  return (
    <div data-testid="agent-demo-page" className="p-10 max-w-6xl">
      <Toaster position="top-right" richColors />

      <Link to={`/agents/${id}`} className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to agent editor
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Demo mode
          </div>
          <h1 className="font-display text-4xl font-black tracking-tight">{agent.name}</h1>
          {agent.tagline && <p className="text-neutral-600 mt-2">{agent.tagline}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="agent-demo-reset-btn"
            onClick={reset}
            className="lf-btn-outline flex items-center gap-2 text-sm"
          >
            <RotateCcw className="w-4 h-4" /> Reset conversation
          </button>
          <button
            data-testid="agent-demo-copy-link-btn"
            onClick={copyShareUrl}
            className="lf-btn-outline flex items-center gap-2 text-sm"
          >
            <Copy className="w-4 h-4" /> Copy shareable demo link
          </button>
          <a
            data-testid="agent-demo-open-standalone"
            href={shareUrl}
            target="_blank"
            rel="noreferrer"
            className="lf-btn-primary flex items-center gap-2 text-sm"
          >
            <ExternalLink className="w-4 h-4" /> Open standalone
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* CHAT */}
        <div className="lg:col-span-2 bg-white border border-neutral-200 flex flex-col" style={{ height: 620 }}>
          <div className="px-5 py-3 border-b border-neutral-100 flex items-center gap-3">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center">
              <Bot className="w-4 h-4" />
            </div>
            <div>
              <div className="font-semibold text-sm">{agent.name}</div>
              <div className="text-xs text-neutral-500">
                {busy ? "typing…" : convId ? `conv ${convId.slice(0, 8)}…` : "fresh conversation"}
              </div>
            </div>
          </div>

          <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-neutral-50" data-testid="agent-demo-transcript">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center ${m.role === "user" ? "bg-neutral-800 text-white" : "bg-amber-500 text-black"}`}>
                  {m.role === "user" ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                </div>
                <div className={`max-w-[80%] px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-neutral-800 text-white" : "bg-white border border-neutral-200"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex gap-3">
                <div className="w-7 h-7 bg-amber-500 text-black flex items-center justify-center">
                  <Bot className="w-3.5 h-3.5" />
                </div>
                <div className="bg-white border border-neutral-200 px-3.5 py-2.5 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-neutral-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-1.5 h-1.5 bg-neutral-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-1.5 h-1.5 bg-neutral-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t border-neutral-100 p-3 flex gap-2">
            <input
              data-testid="agent-demo-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type as if you're the customer…"
              className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-black"
              disabled={busy}
            />
            <button
              data-testid="agent-demo-send-btn"
              type="submit"
              disabled={busy || !input.trim()}
              className="lf-btn-primary flex items-center gap-2 text-sm disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4">
          <div className="bg-white border border-neutral-200 p-5">
            <div className="overline mb-3">Quick prompts</div>
            <div className="flex flex-col gap-2">
              {quickPrompts.map((p) => (
                <button
                  key={p}
                  data-testid={`agent-demo-quick-${p.slice(0, 8).replace(/\W/g, "-")}`}
                  onClick={() => { setInput(p); }}
                  className="text-left text-sm px-3 py-2 border border-neutral-200 hover:border-black hover:bg-neutral-50 transition"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-neutral-200 p-5">
            <div className="overline mb-3">Share this demo</div>
            <p className="text-sm text-neutral-600 mb-3 leading-relaxed">
              Give this link to a prospect and they&apos;ll see only the agent — no fake business
              site around it. Perfect for a &ldquo;here&apos;s how it works&rdquo; moment on a sales call.
            </p>
            <code className="block text-xs bg-neutral-50 border border-neutral-200 p-2 break-all mb-3">
              {shareUrl}
            </code>
            <button
              data-testid="agent-demo-sidebar-copy"
              onClick={copyShareUrl}
              className="lf-btn-outline w-full text-sm flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" /> Copy link
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-800 mb-2">
              Pro tip
            </div>
            <p className="text-sm text-amber-900 leading-relaxed">
              Screen-share this page during your sales call. Prospects convert 3× more when they
              see the agent respond live vs. just being told what it does.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
